import React, { createContext, useContext, useEffect, useState } from 'react';
import { node } from 'prop-types';
import useGuestInfo from '@/usecase/use-guest-info';
import useQueryString from '@/hooks/useQueryString';

import { setItem, getItem } from '@/helpers/localstorage';
import { noop } from '@invitato/helpers';

const ID_LS_GUEST_CODE = 'guestCode';

// GUEST CONTEXT INITIALIZATION
const GuestContext = createContext({ guest: {}, onUpdateGuest: noop });

/**
 * @function AuthProvider Custom provider to store guest information
 * @returns {JSX.Element}
 */
function GuestProvider({ children }) {
  const { getQueryValue } = useQueryString();

  const guestCodeFromQueryString = getQueryValue('code');

  const [currentCode, setCurrentCode] = useState(guestCodeFromQueryString);
  const { isRegistered, isLoading, isCompleted, onRefetchGuest, ...guestInfo } =
    useGuestInfo(currentCode);

  const handleUpdateGuest = (guestCode) => {
    setCurrentCode(guestCode);
    setItem(
      ID_LS_GUEST_CODE,
      JSON.stringify({
        code: guestCode,
        link: window.location.href,
      }),
    );
  };

  // USECASE: GROUP LINK
  // THIS SIDE EFFECT HELP US TO CHECK GUEST CODE IN LOCAL STORAGE
  // IF THERE ANY GUEST CODE IN LOCAL STORAGE AND CURRENT LINK HAS `code`
  // THEN WE NEED TO FORCE UPDATE THE CURRENT GUEST CODE FROM LOCALSTORAGE
  useEffect(() => {
    if (guestCodeFromQueryString) {
      const guestCodeFromLS = getItem(ID_LS_GUEST_CODE);

      // MAKE SURE THIS CODE ONLY WORKS FOR GROUP LINK INVITATION
      if (guestCodeFromLS) {
        try {
          const cacheData = JSON.parse(guestCodeFromLS);
          cacheData.link === window.location.href && setCurrentCode(cacheData.code);
        } catch {
          console.error('FAILED TO PARSE');
        }
      }
    }
  }, [guestCodeFromQueryString]);

  return (
    <GuestContext.Provider
      value={{
        isCompleted,
        guest: guestInfo,
        isLoading: isLoading,
        isRegistered: isRegistered,
        isGroupInvitation: guestInfo.is_group_link,
        onUpdateGuest: handleUpdateGuest,
        onRefetchGuest,
      }}
    >
      {children}
    </GuestContext.Provider>
  );
}

/**
 * Custom hook to return
 * @returns {GuestType}
 */
const useGuest = () => {
  const context = useContext(GuestContext);

  if (typeof context === 'undefined') {
    throw new Error('useGuest() should be used inside <GuestContext />');
  }

  return context;
};

GuestProvider.propTypes = {
  children: node.isRequired,
};

export { GuestProvider, useGuest };
